<template>
  <div>
    <BaseAlert color="yellow">
      <p>
        Montant maximal de prise en charge :
        <span class="font-semibold">{{ claim.compensationValue }} €</span>
      </p>
    </BaseAlert>

    <Selector
      class="mt-5"
      id="handling_type"
      label="Type de prise en charge"
      name="handling_type"
      v-model="handlingType"
      :options="handlingTypeOptions"
    />

    <Selector
      v-if="repairOrReplacement(handlingType)"
      class="mt-5"
      id="claim_handler"
      label="Entité qui va s'occuper de résoudre/gérer le sinistre"
      name="claimHandler"
      v-model="claimHandler"
      :options="claimHandlerOptions"
    />

    <FormField
      v-if="repairOrReplacement(handlingType) && claimHandler === 'merchant'"
      class="mt-5"
      id="claim_handler_email"
      label="Email du gestionnaire"
      name="claimHandlerEmail"
      v-model="claimHandlerEmail"
      type="email"
    />

    <Selector
      class="mt-5"
      id="refunded_entity"
      label="Entité remboursée"
      name="refundedEntity"
      v-model="refundedEntity"
      :options="refundedEntityOptions"
    />

    <RadioButtons
      v-if="showNotifyCustomer"
      class="mt-5"
      :label="notifyCustomerLabel"
      name="notifyCustomer"
      v-model="notifyCustomer"
      :valueOptions="notifyCustomerOptions"
    />

    <div class="mt-5 flex items-center gap-2">
      <input
        id="has_product_return"
        type="checkbox"
        v-model="hasProductReturn"
        class="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600"
      />
      <label for="has_product_return" class="text-sm text-gray-700">
        Lancer la procédure de récupération du produit
      </label>
    </div>

    <template v-if="hasProductReturn">
      <FormField
        class="mt-5"
        id="height_cm"
        label="Hauteur (cm)"
        name="heightCm"
        v-model="heightCm"
        type="number"
        :step="0.1"
      />
      <FormField
        class="mt-5"
        id="width_cm"
        label="Largeur (cm)"
        name="widthCm"
        v-model="widthCm"
        type="number"
        :step="0.1"
      />
      <FormField
        class="mt-5"
        id="length_cm"
        label="Longueur (cm)"
        name="lengthCm"
        v-model="lengthCm"
        type="number"
        :step="0.1"
      />
      <FormField
        class="mt-5"
        id="weight_kg"
        label="Poids (kg)"
        name="weightKg"
        v-model="weightKg"
        type="number"
        :step="0.01"
      />
      <FormField
        class="mt-5"
        id="product_url"
        label="URL du produit"
        name="productUrl"
        v-model="productUrl"
        type="url"
      />
    </template>

    <div class="flex justify-center">
      <GenericButton @click="update" v-if="handlingType" class="mt-5">Valider</GenericButton>
    </div>
  </div>
</template>

<script>
import { GenericButton, Selector, FormField, BaseAlert, RadioButtons } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "HandlingTypeForm",

  components: { BaseAlert, Selector, GenericButton, FormField, RadioButtons },
  props: ["claim", "defaultValues"],
  emits: ["submitted"],

  data: () => {
    return {
      handlingType: null,
      refundedEntity: null,
      claimHandler: null,
      claimHandlerEmail: null,
      notifyCustomer: false,
      notifyCustomerOptions: [
        { value: true, label: "Oui" },
        { value: false, label: "Non" },
      ],
      hasProductReturn: false,
      heightCm: null,
      widthCm: null,
      lengthCm: null,
      weightKg: null,
      productUrl: null,
    };
  },

  computed: {
    ...mapGetters("claims", [
      "handlingTypeOptions",
      "refundedEntityOptions",
      "claimHandlerOptions",
      "repairOrReplacement",
      "repair",
      "storeHandleRepair",
    ]),
    shouldPrefillVoucher() {
      return !!this.claim?.store?.voucherProvider;
    },
    showNotifyCustomer() {
      if (this.repair(this.handlingType) && this.claimHandler === "merchant") return true;
      if (this.handlingType === "replacement" && this.claim?.unfixeableClaimProduct) return true;
      return false;
    },
    notifyCustomerLabel() {
      if (this.handlingType === "replacement") {
        return "Envoyer un mail au client pour confirmer la prise en charge du remplacement";
      }
      return "Envoyer un mail au client pour confirmer la prise en charge de la réparation";
    },
  },

  methods: {
    ...mapActions("claims", ["specifyHandlingType"]),

    prefillFromClaim() {
      const c = this.claim || {};
      this.handlingType = c.handlingType ?? null;
      this.refundedEntity = c.refundedEntity ?? null;
      this.claimHandler = c.claimHandler ?? null;
      this.claimHandlerEmail = c.claimManager ?? null;
      this.heightCm = this.claim?.claimProduct?.heightCm ?? null;
      this.widthCm = this.claim?.claimProduct?.widthCm ?? null;
      this.lengthCm = this.claim?.claimProduct?.lengthCm ?? null;
      this.weightKg = this.claim?.claimProduct?.weightKg ?? null;
      this.productUrl = this.claim?.claimProduct?.productUrl ?? null;
      if (this.shouldPrefillVoucher) {
        this.handlingType = "voucher";
        this.hasProductReturn = true;
      }
      if (this.defaultValues) {
        this.handlingType = this.defaultValues.handlingType;
        this.claimHandler = this.defaultValues.claimHandler;
      }
    },

    async update() {
      const params = {
        handlingType: this.handlingType,
        refundedEntity: this.refundedEntity,
        claimHandler: this.claimHandler,
        claimHandlerEmail: this.claimHandlerEmail,
        setupPayment: true,
      };
      if (this.showNotifyCustomer && this.notifyCustomer) {
        params.notifyCustomer = true;
      }
      if (this.hasProductReturn) {
        params.heightCm = Number(this.heightCm);
        params.widthCm = Number(this.widthCm);
        params.lengthCm = Number(this.lengthCm);
        params.weightKg = Number(this.weightKg);
        params.productUrl = this.productUrl;
      }
      const { success } = await this.specifyHandlingType(params);
      if (success) this.$emit("submitted");
    },
  },

  created() {
    this.prefillFromClaim();
  },

  watch: {
    // Les données du sinistre peuvent changer pendant que le formulaire est affiché.
    claim: {
      deep: true,
      handler() {
        this.prefillFromClaim();
      },
    },
    handlingType(newValue) {
      this.notifyCustomer = false;
      this.hasProductReturn = newValue === "voucher" && this.shouldPrefillVoucher;
      this.heightCm = null;
      this.widthCm = null;
      this.lengthCm = null;
      this.weightKg = null;
      this.productUrl = null;
      if (newValue === "reimbursement") {
        this.refundedEntity = "customer";
        this.claimHandler = null;
      } else if (newValue === "repair") {
        if (!this.claim?.store) return;

        if (this.storeHandleRepair(this.claim)) {
          this.claimHandler = "merchant";
        } else {
          this.claimHandler = "external_merchant";
        }
      } else if (newValue === "voucher") {
        this.refundedEntity = "merchant";
        this.claimHandler = null;
      }
    },
    claimHandler(newValue) {
      if (newValue === "external_merchant") {
        this.refundedEntity = "customer";
        this.claimHandlerEmail = null;
      } else if (newValue === "merchant") {
        this.refundedEntity = "merchant";
        this.claimHandlerEmail = this.claim?.store?.repairWorkshopEmail || this.claim?.claimManager;
      } else {
        this.claimHandlerEmail = null;
      }
    },
  },
};
</script>
